import React, { useState, Fragment } from "react";

import { t } from "i18next";
import { Trans } from "react-i18next";
import { cloneDeep, isEmpty } from "lodash";
import Select from "react-select";

import ParameterUnitsCards from "./ParameterUnitsCards";
import ValidationError from "../Shared/ValidationError";
import {
    MAX_PARAMETER,
    MAX_UNITS_PER_PARAMETER,
    MODEL_PROCESSING_TYPES,
} from "../../utils/constants";
import DraggableArea from "../Shared/DraggableArea";
import { getRoles } from "../../utils/OktaHelpers";
import { isAuthorized } from "../../utils/RolesHelpers";
import { ROLES } from "../../utils/constants";
import {
    getReactSelectStyling,
    getReactSelectTheme,
} from "../../utils/ReactSelectStyles";
import LocalizedLablesCard from "./LocalizedLablesCard";
import { ErrorAlert } from "../../utils/Alerts";

const ParametersCards = ({
    parameters,
    setParameters,
    addParameterHandler,
    showAddUnit = false,
    showAddParameter = false,
    units,
    conversionUnits,
    setConversionUnits,
    handleBaseUnitChange,
    activeUnit,
    setActiveUnit,
    error,
    isFormSubmitted,
    isUnitsLoading,
    conversionUnitsLoading,
    disableEditingName = false,
    hideParameterDeleteIcon = false,
    isEdit = false,
    showClassificationOption = false,
    updateClassificationParameters = () => {},
    parameterUnitsMaps,
    isDynamicSorted,
    isCustom,
}) => {
    const [apiLanguages, setApiLanguages] = useState({});

    const addEmptyParameterUnitCard = (index) => {
        const newParam = cloneDeep(parameters)[index];
        const condition = isEdit
            ? isEditUnitChangable()
            : !parameterUnitsMaps?.[newParam.name];
        if (condition) {
            setParameters((prevParams) => {
                const newParams = cloneDeep(prevParams);
                newParams[index].parameterDetails.push({
                    unit: "",
                    decimalPlaces: 2,
                });
                return newParams;
            });
        } else {
            ErrorAlert(t("unchangableUnitMessage"));
        }
    };

    const removerParameter = (index) => {
        setActiveUnit((prevUnits) => {
            const newUnits = [...prevUnits];
            newUnits.splice(index, 1);
            return newUnits;
        });

        const newParams = [...parameters];
        newParams.splice(index, 1);

        updateClassificationParameters(newParams);
        setParameters(newParams);
    };

    const removeParameterUnitCard = (parameterIndex, unitIndex) => {
        const newParams = cloneDeep(parameters);
        const isDeletable = isUnitChangable(
            newParams[parameterIndex].name,
            newParams[parameterIndex].parameterDetails[unitIndex].unit?.label
        );
        const condition = isEdit ? isEditUnitChangable() : isDeletable;
        if (condition) {
            const slicedUnit = newParams[
                parameterIndex
            ].parameterDetails.splice(unitIndex, 1);
            setParameters(newParams);
            setConversionUnits((prevUnits) => {
                const newUnits = [...prevUnits];
                if (slicedUnit[0].unit) {
                    newUnits[parameterIndex].push(slicedUnit[0].unit);
                }
                return newUnits;
            });
            handleActiveUnitRemoval(parameterIndex, unitIndex);
        } else {
            ErrorAlert(t("unchangableUnitMessage"));
        }
    };

    const handleActiveUnitRemoval = (parameterIndex, unitIndex) => {
        setActiveUnit((prevActive) => {
            const newUnits = [...prevActive];
            const newActiveIndex =
                activeUnit[parameterIndex] === unitIndex
                    ? unitIndex - 1
                    : newUnits[parameterIndex] - 1;
            newUnits[parameterIndex] = Math.max(1, newActiveIndex);
            return newUnits;
        });
    };

    const handleParameterDetailsChange = (
        parameterIndex,
        unitIndex,
        key,
        value
    ) => {
        let newParams = [...parameters];
        newParams[parameterIndex].parameterDetails[unitIndex][key] = value;
        setParameters(newParams);
    };

    const isEditUnitChangable = () => {
        return isEdit && !isDynamicSorted;
    };

    const isUnitChangable = (parameterName, currentUnitId) => {
        return (
            !parameterUnitsMaps?.[parameterName] ||
            parameterUnitsMaps[parameterName] !== currentUnitId
        );
    };

    const handleUnitsChange = (
        parameterIndex,
        unitIndex,
        value,
        isBaseUnit = false
    ) => {
        let newParams = [...parameters];
        const isChangable = isUnitChangable(
            newParams[parameterIndex].name,
            newParams[parameterIndex].parameterDetails[unitIndex].unit?.label
        );
        const isBaseUnitChangable = !(
            parameterUnitsMaps?.[newParams[parameterIndex].name] &&
            unitIndex === 0
        );
        const condition = isEdit
            ? isEditUnitChangable()
            : isBaseUnitChangable && isChangable;

        if (condition) {
            if (isBaseUnit) {
                handleBaseUnitChange(parameterIndex, value);
                const decimalPlaces =
                    newParams[parameterIndex].parameterDetails[unitIndex]
                        .decimalPlaces;

                newParams[parameterIndex].parameterDetails = [
                    { unit: value, decimalPlaces },
                ];
            } else {
                setConversionUnits((prevUnits) => {
                    const newUnits = [...prevUnits];
                    newUnits[parameterIndex] = newUnits[parameterIndex].filter(
                        (unit) => unit.value !== value.value
                    );
                    return newUnits;
                });
                newParams[parameterIndex].parameterDetails[unitIndex].unit =
                    value;
            }
            setParameters(newParams);
        } else {
            ErrorAlert(t("unchangableUnitMessage"));
        }
    };

    const handleOnChange = (e, index) => {
        setParameters((prevParams) => {
            const newParams = [...prevParams];
            newParams[index][e.target.name] = e.target.value;
            return newParams;
        });
    };

    const handleOnChangeUsingName = (name, value, index) => {
        setParameters((prevParams) => {
            const newParams = [...prevParams];
            newParams[index][name] = value;
            return newParams;
        });
    };

    const handleSelectChange = (name, value, index) => {
        setParameters((prevParams) => {
            const newParams = [...prevParams];
            newParams[index][name] = value;
            updateClassificationParameters(newParams);
            return newParams;
        });
    };

    const isAddUnitShown = () => {
        return showAddUnit && !isEmpty(conversionUnits);
    };

    const isAddUnitDisabled = (parameter, index) => {
        const parameterLength = parameter.parameterDetails?.length;
        const isPrevEmpty =
            parameter.parameterDetails[parameterLength - 1]?.unit;

        return (
            parameterLength > MAX_UNITS_PER_PARAMETER ||
            !isPrevEmpty ||
            !conversionUnits[index]?.length
        );
    };

    const renderUnitsGuide = () => {
        return (
            <div className="flex flex-col items-end">
                <div>
                    <div className="flex">
                        <div className="w-4 h-3 border bg-primary-color-blue mr-2"></div>
                        <h4 className="text-xxs">
                            <Trans i18nKey="additionalUnits">
                                Additional units.
                            </Trans>
                        </h4>
                    </div>
                    <div className="flex">
                        <div className="w-4 h-3 border bg-primary-color-orange mr-2"></div>
                        <h4 className="text-xxs">
                            <Trans i18nKey="baseUnitDescription">
                                Base unit (can&apos;t be modified)
                            </Trans>
                        </h4>
                    </div>
                </div>
            </div>
        );
    };

    const getParameterTypes = () => {
        let types = { ...MODEL_PROCESSING_TYPES };
        if (!isAuthorized([ROLES.support], getRoles()) && types?.Classification)
            delete types.Classification;
        return Object.values(types);
    };

    const renderParameterContent = (parameter, index) => {
        return (
            <>
                <div className="flex w-full items-center item-list grey-list my-2">
                    <div className="flex flex-col w-full pt-3">
                        <div className="items flex justify-between">
                            <div className="input-group w-full">
                                <div className="tags pl-3 pb-3 whitespace-wrap">
                                    <h4 className="uppercase text-xs required">
                                        <Trans i18nKey="technicalName">
                                            Technical Name
                                        </Trans>
                                    </h4>
                                    <input
                                        className={`input-element ${
                                            isCustom &&
                                            parameterUnitsMaps[parameter.name]
                                                ? "disabled"
                                                : ""
                                        }`}
                                        type="text"
                                        name="name"
                                        disabled={
                                            disableEditingName ||
                                            (isCustom &&
                                                parameterUnitsMaps[
                                                    parameter.name
                                                ])
                                        }
                                        value={parameter.name || ""}
                                        placeholder={t("technicalName")}
                                        onBlur={() =>
                                            showClassificationOption &&
                                            updateClassificationParameters()
                                        }
                                        onChange={(e) =>
                                            handleOnChange(e, index)
                                        }
                                    />
                                    {isFormSubmitted && (
                                        <ValidationError
                                            errorMessage={
                                                error[
                                                    `${index.toString()}.name`
                                                ]
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                            {showClassificationOption && (
                                <div className="input-group w-full">
                                    <div className="tags pl-3 pb-3 whitespace-wrap">
                                        <h4 className="uppercase text-xs required">
                                            <Trans i18nKey="type">Type</Trans>
                                        </h4>
                                        <Select
                                            options={getParameterTypes()}
                                            value={
                                                parameter.type ||
                                                MODEL_PROCESSING_TYPES.Regression
                                            }
                                            onChange={(value) => {
                                                handleSelectChange(
                                                    "type",
                                                    value,
                                                    index
                                                );
                                            }}
                                            isDisabled={
                                                hideParameterDeleteIcon ||
                                                (isCustom &&
                                                    parameterUnitsMaps[
                                                        parameter.name
                                                    ])
                                            }
                                            placeholder={t("type")}
                                            styles={getReactSelectStyling()}
                                            theme={getReactSelectTheme}
                                        />
                                        {isFormSubmitted && (
                                            <ValidationError
                                                errorMessage={
                                                    error[
                                                        `${index.toString()}.type`
                                                    ]
                                                }
                                            />
                                        )}
                                    </div>
                                </div>
                            )}
                            {parameters.length > 1 && !hideParameterDeleteIcon && (
                                <div
                                    className="icon-wrapper text-sm mt-1"
                                    onClick={() => removerParameter(index)}
                                >
                                    <i className="fas fa-times" />
                                </div>
                            )}
                        </div>
                        <div className="px-3">
                            <LocalizedLablesCard
                                localizations={
                                    parameter?.localizations || { en: "" }
                                }
                                apiLanguages={apiLanguages}
                                setApiLanguages={setApiLanguages}
                                setLocalizations={(val) =>
                                    handleOnChangeUsingName(
                                        "localizations",
                                        val,
                                        index
                                    )
                                }
                                mainLanguageErrorMessage={
                                    isFormSubmitted &&
                                    error[
                                        `${index.toString()}.localizations.en`
                                    ]
                                }
                                label={t("displayName")}
                            />
                        </div>

                        <div className="items">
                            <ParameterUnitsCards
                                parameter={parameter}
                                parameterUnits={parameter.parameterDetails}
                                handleParameterDetailsChange={
                                    handleParameterDetailsChange
                                }
                                handleUnitsChange={handleUnitsChange}
                                removeUnitHandler={removeParameterUnitCard}
                                parameterIndex={index}
                                activeUnit={activeUnit}
                                setActiveUnit={setActiveUnit}
                                error={error}
                                isFormSubmitted={isFormSubmitted}
                                units={units}
                                conversionUnits={conversionUnits}
                                isEdit={isEdit}
                                conversionUnitsLoading={conversionUnitsLoading}
                                isUnitsLoading={isUnitsLoading}
                                parameterUnitsMaps={parameterUnitsMaps}
                                isDynamicSorted={isDynamicSorted}
                            />
                        </div>
                        {isAddUnitShown() && (
                            <div className="flex justify-end mr-4 p-2">
                                <span
                                    onClick={() =>
                                        addEmptyParameterUnitCard(index)
                                    }
                                    className={`hyper-btn text-xs ${
                                        isAddUnitDisabled(parameter, index)
                                            ? "disabled"
                                            : ""
                                    }`}
                                >
                                    <i className="fas fa-plus fa-xs mr-1"></i>
                                    <Trans i18nKey="addUnit">Add Unit</Trans>
                                </span>
                            </div>
                        )}
                    </div>
                    <div>
                        <i
                            className={`fas fa-grip-vertical text-secondary-color-light-blue ${
                                parameters.length > 1 ? "" : "invisible"
                            }`}
                        ></i>
                    </div>
                </div>
            </>
        );
    };

    const renderParameters = () => {
        const parameterNodes =
            parameters &&
            parameters.map((parameter, index) => {
                return (
                    <Fragment key={index}>
                        {renderParameterContent(parameter, index)}
                    </Fragment>
                );
            });
        return (
            <DraggableArea
                items={parameters}
                setItems={setParameters}
                droppableId="parameter-cards"
                draggableContent={parameterNodes}
            />
        );
    };

    return (
        <>
            {renderUnitsGuide()}
            {renderParameters()}
            {showAddParameter && (
                <div className={"flex justify-end p-2"}>
                    <span
                        onClick={addParameterHandler}
                        className={`hyper-btn text-xs ${
                            parameters.length >= MAX_PARAMETER ? "disabled" : ""
                        }`}
                    >
                        <i className="fas fa-plus fa-xs mr-1"></i>
                        <Trans i18nKey="addParameter">Add Parameter</Trans>
                    </span>
                </div>
            )}
        </>
    );
};

export default ParametersCards;
